'use client';

import { useEffect, useState } from 'react';
import posthog from 'posthog-js';
import type { CapturedNetworkRequest } from '@posthog/types';
import { PostHogProvider } from 'posthog-js/react';
import PostHogErrorBoundary from '../PostHogErrorBoundary';
import {
  getBootstrapFromDocumentCookie,
  getGlobalVisitorEventProperties,
} from 'utils/bootstrapHelpers';
import { redactExceptionEvent } from 'utils/errorTracking';
import useCriticalFlowRecording from 'hooks/useCriticalFlowRecording';
import useWebVitalsFlush from 'hooks/useWebVitalsFlush';

// Initialize PostHog after hydration to prevent script loading mismatches
let isPostHogInitialized = false;
let isGlobalErrorHandlerInstalled = false;
const POSTHOG_DISTINCT_ID_COOKIE = 'ph_distinct_id';

const readDocumentCookie = (name: string): string | undefined => {
  try {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length !== 2) return undefined;
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue ? decodeURIComponent(cookieValue) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Checks if an error is a PostHog-related stack overflow error.
 * These errors occur in PostHog's internal queue processing and can't be caught by React error boundaries.
 */
const isPostHogStackOverflow = (error: Error | string): boolean => {
  const message = typeof error === 'string' ? error : error?.message || '';
  const stack = typeof error === 'string' ? '' : error?.stack || '';

  // Must be a stack overflow error
  if (!message.includes('Maximum call stack size exceeded')) {
    return false;
  }

  // Check for PostHog patterns in the stack trace
  const postHogPatterns = [
    'posthog',
    'PostHog',
    '_execute_array',
    'getFeatureFlag',
    'isFeatureEnabled',
    'onFeatureFlags',
    'np.<anonymous>',
    'np.push',
    'np._execute_array',
    // Minified patterns
    /\bnp\b/,
    /\bth\b/,
  ];

  for (const pattern of postHogPatterns) {
    if (typeof pattern === 'string') {
      if (stack.includes(pattern)) return true;
    } else if (pattern.test(stack)) {
      return true;
    }
  }

  // If it's a stack overflow and we have PostHog loaded, assume it's PostHog-related
  // This is a safe assumption since our app code shouldn't cause stack overflows
  return isPostHogInitialized;
};

/**
 * Install a global error handler to catch PostHog stack overflow errors
 * that occur outside React's render cycle (in PostHog's internal queue processing).
 */
const installGlobalErrorHandler = () => {
  if (typeof window === 'undefined' || isGlobalErrorHandlerInstalled) return;

  isGlobalErrorHandlerInstalled = true;

  // Handle uncaught errors
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    if (error && isPostHogStackOverflow(error)) {
      // eslint-disable-next-line no-console
      console.warn('[PHProvider] Suppressed PostHog stack overflow error:', message);
      return true; // Prevent default error handling (page crash)
    }
    // Call original handler if it exists
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };

  // Handle unhandled promise rejections
  const originalOnUnhandledRejection = window.onunhandledrejection;
  window.onunhandledrejection = (event) => {
    const error = event.reason;
    if (error && isPostHogStackOverflow(error)) {
      // eslint-disable-next-line no-console
      console.warn('[PHProvider] Suppressed PostHog unhandled rejection:', error?.message);
      event.preventDefault();
      return;
    }
    // Call original handler if it exists
    if (originalOnUnhandledRejection) {
      originalOnUnhandledRejection.call(window, event);
    }
  };
};

// Check if we should enable PostHog (only in production to avoid dev issues)
const shouldEnablePostHog = () => {
  if (typeof window === 'undefined') return false;
  // Disable in development to prevent stack overflow and fetch loop issues
  if (process.env.NODE_ENV === 'development') return false;
  // Must have a PostHog key
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return false;
  return true;
};

const initializePostHog = () => {
  if (isPostHogInitialized || typeof window === 'undefined') return;
  if (!shouldEnablePostHog()) {
    isPostHogInitialized = true;
    return;
  }

  try {
    // Expose posthog globally for console debugging
    (window as any).posthog = posthog;

    // Read bootstrap data from cookie (set by proxy.ts server-side evaluation)
    const bootstrapData = getBootstrapFromDocumentCookie();
    const distinctID =
      bootstrapData?.distinctId ||
      readDocumentCookie(POSTHOG_DISTINCT_ID_COOKIE) ||
      readDocumentCookie('ph_phc_distinct_id');
    const bootstrap = {
      distinctID,
      isIdentifiedID: false,
      featureFlags: bootstrapData?.featureFlags || {},
      featureFlagPayloads: {},
    };

    // Sanitize the host: a stray trailing newline/whitespace in the env value
    // (seen as "https://app.posthog.com\n" in .env.vercel) yields a malformed
    // api_host and silently breaks ingestion (PD audit #9). trim() + strip any
    // trailing slash so capture always hits a valid ingest URL.
    const posthogHost = (process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com')
      .trim()
      .replace(/\/+$/, '');

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY || '', {
      api_host: posthogHost || 'https://us.i.posthog.com',
      loaded: () => {
        try {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[PostHog] Initialized with session replay privacy masking');
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('PostHog loaded callback failed:', err);
        }
      },
      capture_pageview: false,
      // TODO(PD-34558): gate heatmaps by excluded paths once helper is added
      enable_heatmaps: true,
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
          text: true,
          search: true,
        },
        maskTextSelector:
          '.pii, .sensitive, [data-mask], [data-pii], [data-testid="email"], [data-testid="username"], [data-testid="address"]',
        blockSelector: '[data-block-recording], .no-record',
        maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
          request.requestBody = null;
          request.responseBody = null;
          return request;
        },
        recordHeaders: false,
        recordBody: false,
      },
      persistence: 'localStorage+cookie',
      person_profiles: 'identified_only',
      autocapture: false,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false,
      },
      before_send: redactExceptionEvent,
      // TODO(PD-35243): web_vitals enabled; flush hook in useWebVitalsFlush ensures INP/CLS reach PostHog on mobile pagehide.
      capture_performance: {
        web_vitals: true,
        web_vitals_allowed_metrics: ['LCP', 'CLS', 'INP', 'FCP'],
      },
      disable_surveys: true,
      advanced_disable_flags: false,
      on_request_error: (err) => {
        // eslint-disable-next-line no-console
        console.warn('PostHog request failed:', err);
      },
      ...(bootstrapData ? { bootstrap } : {}),
    });

    // Register before ProviderWrapper emits its manual pageview and before
    // child feature flags emit exposure events. The proxy computes this on
    // every landing path, so it must not be gated by Homepage rendering.
    const visitorEventProperties = getGlobalVisitorEventProperties(bootstrapData);
    if (visitorEventProperties) {
      posthog.register(visitorEventProperties);
    }

    isPostHogInitialized = true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('PostHog initialization failed:', err);
    isPostHogInitialized = true; // Prevent retry
  }
};

const PHProvider = ({ children }: { children: React.ReactNode }) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);

    // Install global error handler FIRST to catch PostHog stack overflow errors
    // that occur outside React's render cycle (in PostHog's internal queue processing)
    installGlobalErrorHandler();

    // Only initialize PostHog in production
    if (shouldEnablePostHog()) {
      // Initialize immediately after hydration - no artificial delays
      // PostHog handles its own async initialization gracefully
      // Feature flags need to be available ASAP for A/B tests
      initializePostHog();
    }
  }, []);

  // During SSR and initial hydration, render children without PostHog
  if (!isHydrated) {
    return <>{children}</>;
  }

  // In development or without PostHog key, just render children
  if (!shouldEnablePostHog()) {
    return <>{children}</>;
  }

  // After hydration in production, wrap with PostHog provider and error boundary
  // PostHogErrorBoundary catches "Maximum call stack size exceeded" errors
  // from PostHog's feature flag infinite recursion bug and prevents page crashes
  return (
    <PostHogErrorBoundary>
      <PostHogProvider client={posthog}>
        <CriticalFlowRecorder />
        {children}
      </PostHogProvider>
    </PostHogErrorBoundary>
  );
};

const CriticalFlowRecorder = () => {
  useCriticalFlowRecording();
  useWebVitalsFlush();
  return null;
};

export default PHProvider;
